import styled from '@emotion/styled';
import { PlanModel } from '@journeyapps-platform/lib-journey-core';
import { PRODUCT_CONFIG_MAP, Product, ProductConfig } from '@journeyapps-platform/types-accounts';
import { ProjectFeatureFlags } from '@journeyapps-platform/types-accounts-hub';
import * as _ from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { Route, Routes as RoutesSwitch } from 'react-router-dom';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { LayerWidget } from '../../../layers/LayerWidget';
import { Routes, useJourneyNavigation } from '../../../routes';
import { OrgPageWidget } from '../../../widgets/page/OrgPageWidget';
import { PageHeadingWidget } from '../../../widgets/page/PageHeadingWidget';
import { PlanWidget } from './widgets/subscription/PlanWidget';
import { UpgradePlanDialog } from './upgrade-plan-dialog/UpgradePlanDialog';
import { UpdatePlanCardDialogWidget } from './update-plan-card-dialog/UpdatePlanCardDialogWidget';
import { EmptyItemsWidget } from '../../../widgets/EmptyItemsWidget';

export const SubscriptionsPage: React.FC = observer(() => {
  const orgstore = useOrgStore();

  const current_org = orgstore.current_org;

  useEffect(() => {
    current_org?.loadPlans();
    current_org?.loadApps();
  }, [current_org?.id]);
  const { relativeRoute } = useJourneyNavigation(Routes.MANAGE_PLANS);

  const items = _.sortBy(current_org?.plans.items || [], (i) => i.model.label);
  const loadingPlans = current_org?.plans.loading && items.length === 0;
  const loadingApps = current_org?.apps.loading && current_org?.apps.items.length === 0;

  let config = PRODUCT_CONFIG_MAP[Product.APP];

  // check if we have any PowerSync apps
  if (current_org?.apps?.items.find((a) => a.model.features.includes(ProjectFeatureFlags.POWERSYNC))) {
    config = PRODUCT_CONFIG_MAP[Product.POWERSYNC];
  }

  const loading = loadingPlans || loadingApps;

  return (
    <>
      <OrgPageWidget loading={loading} activeItemKey={Routes.MANAGE_PLANS}>
        <PageHeadingWidget title={'Subscriptions'} />
        {loading ? null : <PlansList plans={items} config={config} />}
      </OrgPageWidget>
      <RoutesSwitch>
        <Route
          path={relativeRoute(Routes.UPGRADE_PLAN)}
          element={
            <LayerWidget<Routes.UPGRADE_PLAN>
              route={Routes.UPGRADE_PLAN}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <UpgradePlanDialog handler={handler} />;
              }}
            />
          }
        />
        <Route
          path={relativeRoute(Routes.PLAN_CARD_SETTINGS)}
          element={
            <LayerWidget<Routes.PLAN_CARD_SETTINGS>
              route={Routes.PLAN_CARD_SETTINGS}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <UpdatePlanCardDialogWidget handler={handler} />;
              }}
            />
          }
        />
      </RoutesSwitch>
    </>
  );
});

export const PlansList: React.FC<{ plans: PlanModel[]; config: ProductConfig }> = ({ plans, config }) => {
  if (plans.length === 0) {
    return <EmptyItemsWidget label="No active subscriptions" />;
  }

  return (
    <>
      {plans.map((p: PlanModel) => {
        return <S.Plan key={p.model.id} plan={p} />;
      })}
    </>
  );
};

namespace S {
  export const Plan = styled(PlanWidget)`
    margin-bottom: 20px;

    &:last-of-type {
      margin-bottom: 0;
    }
  `;
}
